import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { CardEntityError, useFormErrors } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { useEntityFormViewTemplate } from '@shared/scenes/entity/util/useEntityFormViewTemplate';
import { useViewComponents } from '@shared/scenes/entity/util/useViewComponents';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { FieldRecord, UpdateField } from '../../types';
import { useEditFields } from './useEditFields';
import { useSaveEntityFields } from './useSaveCardEntity';

type Status = 'loading' | 'error' | 'input' | 'success';

interface EditCardEntity {
  components: MBAPI.WorkflowTemplate_components[];
  fields: FieldRecord | null;
  errors: CardEntityError[];
  viewTemplateTitle: string;
  viewTemplate?: MBAPI.ViewTemplate;
  updateField: UpdateField;
  onPressSave: () => void;
  isLoading: boolean;
  isSaving: boolean;
  status: Status;
  isDirty: boolean;
  threadId?: string;
  savedEntity?: MBAPI.WorkflowEntity;
}

export function useEditCardEntity({
  entityId,
  workflowTemplateId,
  viewTemplateId,
  isReadOnly = false,
}: {
  entityId: string;
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  isReadOnly?: boolean;
}): EditCardEntity {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const viewTemplate = useEntityFormViewTemplate({
    workflowTemplateId,
    viewTemplateId,
    type: 'EDIT',
  });
  const [status, setStatus] = useState<Status>('loading');
  const [saveAttemptedWithLocalErrors, setSaveAttemptedWithLocalErrors] = useState(false);
  const [savedEntity, setSavedEntity] = useState<MBAPI.WorkflowEntity>();
  const {
    fields,
    modifiedFields,
    updateField,
    isDirty,
    threadId,
    loading: fieldsLoading,
    errors: fieldsErrors,
  } = useEditFields(entityId, workflowTemplate);

  const {
    saveEntity,
    loading: saveEntityLoading,
    errors: apiErrors,
  } = useSaveEntityFields(entityId, workflowTemplate);

  const components = useViewComponents({
    workflowTemplate,
    viewTemplate,
    includeServerManagedComponents: true,
    isReadOnly,
  });

  const { errors, isValid } = useFormErrors({
    apiErrors: [...apiErrors, ...fieldsErrors],
    fields,
    components,
    includeOnSubmitErrors: saveAttemptedWithLocalErrors,
    includeLocalErrors: true,
  });

  useEffect(() => {
    setStatus(prevStatus => {
      const isFieldsReady = !isNil(fields);
      const isViewTemplateReady = !isNil(viewTemplate);
      if (fieldsLoading || !isFieldsReady || !isViewTemplateReady) {
        return 'loading';
      } else if (!isEmpty(errors)) {
        return 'error';
      } else if (prevStatus !== 'success') {
        return 'input';
      } else {
        return prevStatus;
      }
    });
  }, [errors, fieldsLoading, status, fields, viewTemplate]);

  useEffect(() => {
    if (isValid) {
      setSaveAttemptedWithLocalErrors(false);
    }
  }, [isValid]);

  const onPressSave = useCallback(async () => {
    if (!isValid) {
      setSaveAttemptedWithLocalErrors(true);
      return;
    }
    setSaveAttemptedWithLocalErrors(false);

    if (!saveEntityLoading && !isNil(modifiedFields)) {
      try {
        const result = await saveEntity(modifiedFields);
        setSavedEntity(result.data?.updateWorkflowEntity?.entity);
        setStatus('success');
      } catch (error) {
        setStatus('error');
      }
    }
  }, [saveEntityLoading, modifiedFields, saveEntity, isValid, setSavedEntity]);

  return {
    components,
    fields,
    errors,
    viewTemplateTitle: viewTemplate?.name ?? '',
    viewTemplate,
    updateField,
    onPressSave,
    isLoading: status === 'loading',
    isSaving: saveEntityLoading,
    status,
    isDirty,
    threadId,
    savedEntity,
  };
}
